import { useEffect, useMemo, useState } from 'react'
import { apiFetch } from '../services/api'
import { useAuth } from '../context/AuthContext'

const TIPOS = {
  entrada:     { label: 'Entradas',      color: '#10b981', bg: '#d1fae5', icon: '↑' },
  salida:      { label: 'Salidas',       color: '#ef4444', bg: '#fee2e2', icon: '↓' },
  venta:       { label: 'Ventas',        color: '#3b82f6', bg: '#dbeafe', icon: '🛒' },
  cancelacion: { label: 'Cancelaciones', color: '#f59e0b', bg: '#fef3c7', icon: '↩' },
}

export default function Movimientos() {
  const { sucursalActiva } = useAuth()
  const POR_PAGINA = 20

  const [movimientos, setMovimientos] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)

  const [filtros, setFiltros] = useState({
    tipo: '',
    producto: '',
    fechaDesde: '',
    fechaHasta: '',
  })

  const [pagina, setPagina] = useState(1)

  useEffect(() => {
    setPagina(1)
  }, [filtros.tipo, filtros.producto, filtros.fechaDesde, filtros.fechaHasta, sucursalActiva?.id])

  const totalPaginas = useMemo(() => {
    return Math.max(1, Math.ceil(total / POR_PAGINA))
  }, [total, POR_PAGINA])

  useEffect(() => {
    if (pagina > totalPaginas) setPagina(totalPaginas)
  }, [pagina, totalPaginas])

  useEffect(() => {
    cargarMovimientos()
  }, [sucursalActiva?.id, pagina, filtros])

  const cargarMovimientos = async () => {
    setLoading(true)
    try {
      const queryParams = new URLSearchParams({
        sucursal_id: sucursalActiva?.id || 1,
        pagina: String(pagina),
        por_pagina: String(POR_PAGINA),
        ...(filtros.tipo && { tipo: filtros.tipo }),
        ...(filtros.producto && { producto: filtros.producto }),
        ...(filtros.fechaDesde && { desde: filtros.fechaDesde }),
        ...(filtros.fechaHasta && { hasta: filtros.fechaHasta }),
      })

      const data = await apiFetch(`/movimientos?${queryParams}`)

      if (data && Array.isArray(data.items)) {
        setMovimientos(data.items)
        setTotal(data.total || data.items.length)
      } else if (Array.isArray(data)) {
        setMovimientos(data)
        setTotal(data.length)
      } else {
        setMovimientos([])
        setTotal(0)
      }
    } catch (error) {
      console.warn('No se pudieron cargar los movimientos de stock:', error)
      setMovimientos([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }

  const resumen = useMemo(() => {
    return movimientos.reduce((acc, m) => {
      acc[m.tipo] = (acc[m.tipo] || 0) + 1
      return acc
    }, {})
  }, [movimientos])

  const formatearFecha = (f) =>
    new Date(f).toLocaleString('es-CL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })

  const limpiarFiltros = () => {
    setFiltros({ tipo: '', producto: '', fechaDesde: '', fechaHasta: '' })
    setPagina(1)
  }

  if (loading) return (
    <div className="main-content">
      <div className="page-header"><h1 className="page-title">Movimientos de Stock</h1></div>
      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-light, #64748b)' }}>Cargando movimientos...</div>
    </div>
  )

  return (
    <div className="main-content">
      <div className="page-header">
        <h1 className="page-title">Movimientos de Stock</h1>
        <p className="page-subtitle">
          {total} movimientos — {sucursalActiva?.nombre || 'Casa Matriz'}
        </p>
      </div>

      <div className="stats-grid" style={{ marginBottom: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
        {Object.entries(TIPOS).map(([tipo, meta]) => (
          <div
            key={tipo}
            className="stat-card"
            style={{
              cursor: 'pointer',
              padding: '1.25rem',
              background: '#fff',
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
              borderLeft: `4px solid ${meta.color}`,
              opacity: filtros.tipo && filtros.tipo !== tipo ? 0.5 : 1,
            }}
            onClick={() => {
              setFiltros(f => ({ ...f, tipo: f.tipo === tipo ? '' : tipo }))
              setPagina(1)
            }}
            title="Filtrar por tipo"
          >
            <div className="stat-label" style={{ color: '#64748b', fontSize: '0.875rem' }}>{meta.label}</div>
            <div className="stat-value" style={{ color: meta.color, fontSize: '1.5rem', fontWeight: 'bold' }}>
              {meta.icon} {resumen[tipo] || 0}
            </div>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.4rem', color: 'var(--text-light, #64748b)' }}>
              Tipo
            </label>
            <select
              className="form-input"
              value={filtros.tipo}
              onChange={e => { setFiltros(f => ({ ...f, tipo: e.target.value })); setPagina(1) }}
            >
              <option value="">Todos</option>
              {Object.entries(TIPOS).map(([v, m]) => (
                <option key={v} value={v}>{m.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.4rem', color: 'var(--text-light, #64748b)' }}>
              Producto
            </label>
            <input
              className="form-input"
              type="text"
              placeholder="Buscar por nombre o código..."
              value={filtros.producto}
              onChange={e => { setFiltros(f => ({ ...f, producto: e.target.value })); setPagina(1) }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.4rem', color: 'var(--text-light, #64748b)' }}>
              Desde
            </label>
            <input
              className="form-input"
              type="date"
              value={filtros.fechaDesde}
              onChange={e => { setFiltros(f => ({ ...f, fechaDesde: e.target.value })); setPagina(1) }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.4rem', color: 'var(--text-light, #64748b)' }}>
              Hasta
            </label>
            <input
              className="form-input"
              type="date"
              value={filtros.fechaHasta}
              onChange={e => { setFiltros(f => ({ ...f, fechaHasta: e.target.value })); setPagina(1) }}
            />
          </div>

          <div>
            <button className="btn btn-outline" onClick={limpiarFiltros} style={{ width: '100%' }}>
              Limpiar filtros
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <span style={{ color: 'var(--text-light, #64748b)', fontSize: '0.875rem' }}>
            {total} resultados
          </span>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Tipo</th>
                <th>Producto</th>
                <th>Cantidad</th>
                <th>Stock ant.</th>
                <th>Stock post.</th>
                <th>Usuario</th>
                <th>Nota</th>
              </tr>
            </thead>
            <tbody>
              {movimientos.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light, #64748b)' }}>
                    {total === 0 ? 'No hay movimientos registrados' : 'No hay movimientos con los filtros aplicados'}
                  </td>
                </tr>
              ) : (
                movimientos.map((m) => {
                  const meta = TIPOS[m.tipo] || { label: m.tipo, color: '#6b7280', bg: '#f3f4f6', icon: '•' }
                  const esPositivo = ['entrada', 'cancelacion'].includes(m.tipo)

                  return (
                    <tr key={m.id}>
                      <td style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{formatearFecha(m.created_at)}</td>
                      <td>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                          padding: '0.25rem 0.6rem', borderRadius: '999px',
                          fontSize: '0.75rem', fontWeight: '600',
                          color: meta.color, background: meta.bg
                        }}>
                          {meta.icon} {meta.label}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontWeight: '500' }}>{m.productos?.nombre || 'N/A'}</div>
                        {m.productos?.codigo && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-light, #64748b)' }}>{m.productos.codigo}</div>
                        )}
                      </td>
                      <td style={{
                        fontWeight: '700',
                        color: esPositivo ? '#10b981' : '#ef4444'
                      }}>
                        {esPositivo ? '+' : '-'}{m.cantidad}
                      </td>
                      <td style={{ color: 'var(--text-light, #64748b)' }}>{m.stock_antes ?? '-'}</td>
                      <td style={{ fontWeight: '600' }}>{m.stock_despues ?? '-'}</td>
                      <td style={{ fontSize: '0.875rem' }}>{m.perfiles?.nombre || '-'}</td>
                      <td style={{ fontSize: '0.875rem', color: 'var(--text-light, #64748b)', maxWidth: '200px' }}>
                        <span title={m.motivo}>
                          {m.motivo
                            ? (m.motivo.length > 30 ? m.motivo.slice(0, 30) + '…' : m.motivo)
                            : '-'}
                        </span>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPaginas > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
            <button
              className="btn btn-outline"
              onClick={() => setPagina(p => Math.max(1, p - 1))}
              disabled={pagina === 1}
              style={{ padding: '0.5rem 0.75rem' }}
            >
              ← Anterior
            </button>

            {Array.from({ length: totalPaginas }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPaginas || Math.abs(p - pagina) <= 2)
              .map((p, idx, arr) => {
                const prev = idx > 0 ? arr[idx - 1] : null
                const needsEllipsis = prev !== null && prev !== p - 1

                return (
                  <span key={`pagewrap-${p}`} style={{ display: 'inline-flex', gap: '0.5rem' }}>
                    {needsEllipsis && (
                      <span style={{ alignSelf: 'center', color: 'var(--text-light, #64748b)' }}>…</span>
                    )}
                    <button
                      className={`btn ${p === pagina ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => setPagina(p)}
                      style={{ padding: '0.5rem 0.75rem' }}
                    >
                      {p}
                    </button>
                  </span>
                )
              })}

            <button
              className="btn btn-outline"
              onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}
              disabled={pagina === totalPaginas}
              style={{ padding: '0.5rem 0.75rem' }}
            >
              Siguiente →
            </button>
          </div>
        )}
      </div>
    </div>
  )
}